/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useCallback, useEffect, useMemo } from "react";

import {
  checkIfContainsPath,
  UserSubscriptionStatus,
  useSubscriptionsSelector,
  useUserSubprofilesSelector,
} from "@nf/common";
import {
  useSubProfileChangeEvent,
  useRedirectToConsentPage,
  useRouter,
  useCountry,
  useLanguage,
} from "../../hooks";

import { AppConfig, ROUTES, mainChannels, paymentPaths } from "@nf/constants";
interface IRoutProtectorProps {
  children: JSX.Element;
  isLoggedIn?: boolean;
}

export const RouteProtector = ({
  children,
  isLoggedIn,
}: IRoutProtectorProps) => {
  const router = useRouter();
  const { subscriptions } = useSubscriptionsSelector();
  const { Data: subprofilesState } = useUserSubprofilesSelector();

  const country = useCountry();
  const language = useLanguage();

  useSubProfileChangeEvent();

  const currentSubprofile = subprofilesState?.currentSubprofile;

  const shouldRedirectToKidsChannel = useMemo(
    () =>
      currentSubprofile?.Child &&
      checkIfContainsPath(router.asPath, mainChannels),
    [router.asPath, currentSubprofile?.Child]
  );

  const onPopState = useCallback(() => {
    if (
      currentSubprofile?.Child &&
      checkIfContainsPath(location.href, mainChannels)
    ) {
      router.replace(AppConfig.ChannelKids);

      history.pushState(
        null,
        document.title,
        `${location.origin}${router.asPath}`
      );
    }
  }, [currentSubprofile?.Child, router.asPath]);

  useEffect(() => {
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useRedirectToConsentPage(isLoggedIn);

  useEffect(() => {
    const isPaymentPath = checkIfContainsPath(router.asPath, paymentPaths);
    const hasActiveSubscription =
      Array.isArray(subscriptions) &&
      subscriptions?.some(
        (subscription) =>
          subscription.Status === UserSubscriptionStatus.ACTIVE ||
          subscription.Status === UserSubscriptionStatus.CANCELLED // still active until the end of subscription period
      );
    const hasPausedSubscription =
      Array.isArray(subscriptions) &&
      subscriptions?.some(
        (subscription) => subscription.Status === UserSubscriptionStatus.HOLD
      );

    if (isPaymentPath && (hasActiveSubscription || hasPausedSubscription)) {
      router.replace(ROUTES.SETTINGS_SUBSCRIPTION);
    }
  }, [router.asPath, subscriptions]);

  useEffect(() => {
    if (
      isLoggedIn &&
      (router.asPath === ROUTES.BASE ||
        router.asPath.startsWith(`${ROUTES.BASE}?`) || // When redirected from Google or from anywhere
        router.asPath === `/${country}/${language}/`)
    ) {
      router.replace(
        currentSubprofile?.InitialHomePage ?? ROUTES.HOME,
        undefined,
        { withLoadingSplash: true }
      );
    }
  }, [
    isLoggedIn,
    router.asPath,
    country,
    language,
    currentSubprofile?.InitialHomePage,
  ]);

  useEffect(() => {
    if (shouldRedirectToKidsChannel) {
      router.replace(AppConfig.ChannelKids, undefined, {
        withLoadingSplash: true,
      });
    }
  }, [shouldRedirectToKidsChannel]);

  useEffect(() => {
    const urlCountryRegEx = /(\/[a-z]{2}\/)/gi;
    if (
      !urlCountryRegEx.test(router.asPath) &&
      (router.asPath.includes(ROUTES.MOVIE_DETAILS_SCREEN) ||
        router.asPath.includes(ROUTES.SERIES_DETAILS_SCREEN) ||
        router.asPath.includes(ROUTES.COLLECTION_DETAILS_SCREEN))
    ) {
      router.replace(router.asPath, undefined, { withLoadingSplash: true });
    }
  }, [router.asPath]);

  return children;
};

export default RouteProtector;
